/**
 * Usage Analytics — what People Space records about its own use.
 *
 * Reads the aggregates the proxy builds from this app's event stream (`src/analytics/usageSink.ts`
 * writes, `proxy-server/routes/usage.js` aggregates). The proxy enforces
 * `space.people.usage_analytics.view` on the data route, so this view's job on a 403 is to EXPLAIN
 * rather than to hide — the nav item is deliberately ungated, because a tab that silently vanishes
 * is indistinguishable from one that is broken.
 *
 * ── The board is TOP-DOWN (Theresa, Sep 2026) ────────────────────────────────────────────────
 * Which pages get used → which of their tabs → what people do inside them, with the second and
 * third levels closed until somebody asks. It used to be a stack of sections in the order they
 * were built: candidate requests first, "most used views" five screens below it. The answer to
 * the question the page is opened with now fits on one screen, and the detail that used to bury
 * it is one click away. The outcome panels (response times, sent emails, ratings, interview
 * outcomes) moved INSIDE the page each of them measures.
 *
 * ── Why this reads the first-party sink and not Amplitude ────────────────────────────────────
 * The same events go to Amplitude (see `src/analytics/amplitude.ts`), which is where slicing and
 * ad-hoc questions belong. This board answers one narrower question — "is the instrumentation
 * working, and which features are actually used?" — from data we own, with no external key, no
 * second account to hold, and no query quota. It is the page you check when you are wondering
 * whether tracking is alive at all.
 *
 * ── Aggregate, with one named exception ──────────────────────────────────────────────────────
 * This data says which colleague used which feature when, so everything here reports counts and
 * never who. The exception is the per-board panel inside TA Action Boards: a board has exactly one
 * owner, so anonymising it would have been a naming trick rather than a control, and the team
 * asked for it to find where support is needed. It is scoped to the boards server-side and shares
 * this board's permission — see `BoardUsageSection` and `proxy-server/routes/usage.js`. Candidate
 * data never enters at all: the sink sends categories and counts, per the PII rule in
 * `src/analytics/segment.ts`.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, BarChart3, Lock, RefreshCw } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@finn-auto/design-system/atoms/accordion';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { fetchUsageSummary, UsageAccessError, type UsageSummary } from '../services/usageApi';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import { StatTile } from './usageAnalytics/StatTile';
import { TimeSeriesChart } from './usageAnalytics/TimeSeriesChart';
import { RankedBars } from './usageAnalytics/RankedBars';
import { RecommendationsSection } from './usageAnalytics/RecommendationsSection';
import { ALL_AREAS, FilterBar } from './usageAnalytics/FilterBar';
import { PageTree } from './usageAnalytics/PageTree';
import { SectionFeatures } from './usageAnalytics/SectionFeatures';
import { LogDetail, PAGES_WITH_DETAIL, TaBoardDetail } from './usageAnalytics/PageDetail';
import { SourcingAnalytics } from './usageAnalytics/SourcingAnalytics';
import {
  areasInTree,
  buildPageTree,
  filterPageTree,
  allPageKeys,
  type PageNode,
} from './usageAnalytics/usageTree';
import { pageMeta, sidebarAreas } from './usageAnalytics/pageRegistry';
import {
  SERIES_ERROR,
  SERIES_PRIMARY,
  formatCompact,
  formatDay,
  formatPercent,
} from './usageAnalytics/chartTokens';

function Card({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-zinc-500">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function AccessRestricted() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100">
          <Lock className="h-6 w-6 text-zinc-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Access restricted</h2>
          <p className="mt-1 text-sm text-zinc-500">
            Usage analytics are limited to the{' '}
            <span className="font-medium">usage-analytics-viewers</span> group. If you need access,
            ask a People Space admin.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The board is new, so "no data" is the EXPECTED first state rather than a failure. Saying so —
 * and saying when data starts — is the difference between a page that looks broken and one that
 * looks ready.
 */
function AwaitingData() {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-8 text-center">
      <BarChart3 className="mx-auto h-8 w-8 text-zinc-300" />
      <p className="mt-3 text-sm font-medium text-zinc-700">No events recorded yet</p>
      <p className="mx-auto mt-1 max-w-md text-xs text-zinc-500">
        Events are recorded from the moment tracking went live — there is no backfill, so this
        board fills up as people use the space. Come back after a day of normal use.
      </p>
    </div>
  );
}

/** The table view. Every number a tooltip shows is reachable here without hovering at all. */
function DailyTable({ summary }: { summary: UsageSummary }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-zinc-200 text-xs text-zinc-500">
            <th scope="col" className="py-2 pr-4 font-medium">Day</th>
            <th scope="col" className="py-2 pr-4 text-right font-medium">Events</th>
            <th scope="col" className="py-2 text-right font-medium">Active users</th>
          </tr>
        </thead>
        <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
          {[...summary.daily].reverse().map((point) => (
            <tr key={point.date} className="border-b border-zinc-100 last:border-0">
              <td className="py-1.5 pr-4 text-zinc-600">{formatDay(point.date)}</td>
              <td className="py-1.5 pr-4 text-right text-zinc-900">
                {point.events.toLocaleString('en-US')}
              </td>
              <td className="py-1.5 text-right text-zinc-900">
                {point.users.toLocaleString('en-US')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Trends, failures and instrumentation gaps — one collapsed section at the bottom.
 *
 * None of this answers "which page is used"; all of it matters once that question is answered, and
 * all of it used to sit between the reader and the answer.
 */
function HealthSection({ data }: { data: UsageSummary }) {
  return (
    <Accordion type="multiple" className="space-y-2">
      <AccordionItem
        value="health"
        className="overflow-hidden rounded-xl border border-zinc-200 bg-white"
      >
        <AccordionTrigger className="px-4 py-3 hover:no-underline">
          <span className="flex flex-1 flex-col gap-0.5 pr-3 text-left">
            <span className="text-sm font-semibold text-zinc-900">Trends, failures and gaps</span>
            <span className="text-xs font-normal text-zinc-500">
              Activity over time, where the app breaks, and what we have no way of knowing.
            </span>
          </span>
        </AccordionTrigger>
        <AccordionContent
          alwaysMounted={false}
          className="space-y-4 border-t border-solid border-zinc-100 px-4 pt-4 pb-4"
        >
          {/* Two charts, not one with two y-axes: events and active users differ by an order of
              magnitude, and a shared plot would invent a crossing point that means nothing. */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Events per day" description="Every tracked interaction, bucketed by Berlin day.">
              <TimeSeriesChart
                points={data.daily.map((d) => ({ date: d.date, value: d.events }))}
                color={SERIES_PRIMARY}
                seriesLabel="Events"
              />
            </Card>
            <Card title="Active users per day" description="Distinct colleagues who did anything that day.">
              <TimeSeriesChart
                points={data.daily.map((d) => ({ date: d.date, value: d.users }))}
                color={SERIES_PRIMARY}
                seriesLabel="Active users"
              />
            </Card>
          </div>

          <Card title="Daily figures" description="The same data as the charts above, exactly.">
            <DailyTable summary={data} />
          </Card>

          {data.errorSources.length > 0 && (
            <Card
              title="Where things fail"
              description="Client errors by source. A spike here is usually a real regression, not noise."
            >
              <RankedBars
                items={data.errorSources.map((e) => ({ label: e.source, value: e.count }))}
                color={SERIES_ERROR}
                emptyMessage="No client errors recorded — good."
              />
            </Card>
          )}

          {/* Gated on events existing deliberately: with none at all, EVERY instrumented feature
              would be listed as "went unused", which is an artefact of tracking being new rather
              than a finding about the product. */}
          <Card
            title="Recommendations"
            description="What nobody reached for, and what we have no way of knowing."
          >
            <RecommendationsSection observedEvents={data.observedEvents ?? []} />
          </Card>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function UsageBoard() {
  const [days, setDays] = useState(30);
  const [area, setArea] = useState(ALL_AREAS);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<string[]>([]);

  // The refresh and range events only fire on interaction, so a board that is READ and not clicked
  // would appear in its own "went unused" list. Reported once per mount.
  useEffect(() => {
    void logAnalyticsEvent('usage_analytics_viewed');
  }, []);

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['usage-summary', days],
    queryFn: () => fetchUsageSummary(days),
    retry: (count, err) => !(err instanceof UsageAccessError) && count < 2,
  });

  const tree = useMemo(() => (data ? withDetailPages(buildPageTree(data)) : []), [data]);
  const filtered = useMemo(
    () => filterPageTree(tree, { area: area === ALL_AREAS ? null : area, query }),
    [tree, area, query]
  );
  // Offered from what is actually here, not from the whole sidebar: an option leading to an empty
  // list is a promise the board cannot keep.
  const areas = useMemo(() => areasInTree(tree, sidebarAreas()), [tree]);

  /**
   * A search that matched something three levels down should show it, not leave the reader to
   * guess which page to open. Typing therefore expands what survived the filter; clearing it hands
   * control back rather than collapsing what somebody opened by hand.
   */
  const searching = query.trim() !== '';
  const openPages = searching ? allPageKeys(filtered) : open;

  if (error instanceof UsageAccessError) return <AccessRestricted />;
  if (isPending) return <LoadingSpinner />;

  if (error || !data) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-white p-5">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-zinc-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-zinc-900">Could not load usage analytics</p>
            <p className="mt-0.5 text-xs text-zinc-500">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
            <Button size="sm" className="mt-3" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const hasData = data.totals.events > 0;

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-1">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Usage analytics</h1>
          <p className="mt-0.5 max-w-[80ch] text-sm text-zinc-500">
            Which pages of People Space get used, which of their tabs, and what people do inside
            them. Aggregate — counts, never who — except the per-board panel inside TA Action
            Boards, where a board has one owner.
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            void logAnalyticsEvent('usage_analytics_refreshed', { days });
            void refetch();
          }}
          disabled={isFetching}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </header>

      <FilterBar
        days={days}
        onDaysChange={(next) => {
          setDays(next);
          void logAnalyticsEvent('usage_analytics_range_changed', { days: next });
        }}
        area={area}
        onAreaChange={setArea}
        areas={areas}
        query={query}
        onQueryChange={setQuery}
        range={data.range}
        resultCount={filtered.length}
        totalCount={tree.length}
      />

      {/* While refetching, hold the previous render at reduced opacity — no skeleton, no layout
          jump, nothing to re-find once it lands. */}
      <div className={`space-y-5 transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile label="Events" value={formatCompact(data.totals.events)} hint={`Last ${data.range.days} days`} />
          <StatTile
            label="Active users"
            value={formatCompact(data.totals.activeUsers)}
            hint={data.truncated ? 'At least — count was capped' : 'Distinct colleagues'}
          />
          <StatTile
            label="Active days"
            value={`${data.totals.activeDays} / ${data.range.days}`}
            hint="Days with any activity"
          />
          <StatTile
            label="Error rate"
            value={formatPercent(data.totals.errorRate)}
            hint={`${formatCompact(data.totals.errors)} client errors`}
          />
        </div>

        {/*
          `AwaitingData` says the EVENT stream is empty; it does not say there is nothing to see.
          Interview outcomes, response times and the per-board panel read booking and triage
          records, so they answer in exactly this range — and they used to be top-level sections
          that rendered here. The tree stays, carrying the pages that have a deep dive.
        */}
        {!hasData && <AwaitingData />}

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-zinc-900">
            {hasData ? 'Pages, most used first' : 'Pages'}
          </h2>
          <PageTree
            pages={filtered}
            open={openPages}
            onOpenChange={setOpen}
            detailFor={(page) => detailFor(page, days)}
            featuresFor={(section) => <SectionFeatures section={section} days={days} />}
          />
        </section>

        {hasData && <HealthSection data={data} />}
      </div>

      {/* What this board CANNOT show, stated on the board itself. Someone reading an empty
          booking funnel here would otherwise conclude the instrumentation is broken, when in fact
          those events are working and simply cannot reach this sink. */}
      <p className="pt-1 text-xs text-zinc-400">
        The counts above are signed-in staff activity only. Candidate booking{' '}
        <em>events</em> (<code className="text-zinc-500">booking_*</code>) never reach this sink — the
        public <code className="text-zinc-500">/book/:token</code> page has no signed-in user, so they
        are dropped rather than attributed to nobody. That does not mean bookings are missing: what
        candidates actually booked, cancelled and moved is under{' '}
        <strong className="font-medium text-zinc-500">Log → Interview outcomes</strong> above, read
        from the booking records themselves — and the{' '}
        <a href="/interviews" className="text-zinc-500 underline underline-offset-2 hover:text-zinc-700">
          Log
        </a>{' '}
        holds the individual rows. The one thing neither can show is what did NOT happen: a link
        opened that never became a booking leaves no record, so that drop-off exists only in
        Amplitude.
      </p>
      <p className="text-xs text-zinc-400">
        {/* Berlin, like the day buckets. A viewer in another timezone would otherwise read a
            timestamp that disagrees with the days the numbers are grouped into. */}
        Updated{' '}
        {new Date(data.generatedAt).toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Europe/Berlin',
        })}{' '}
        Berlin
        {data.truncated && ' · active-user counts hit the query ceiling and are a floor, not a total'}
      </p>
    </div>
  );
}

function detailFor(page: PageNode, days: number) {
  if (page.key === 'ta-action-boards') return <TaBoardDetail days={days} />;
  if (page.key === 'interviews') return <LogDetail days={days} />;
  return null;
}

/**
 * Make sure a page with a deep dive is in the tree even when it recorded no events.
 *
 * Interview outcomes and the per-board panel read booking and triage records, not the event
 * stream, so they answer in ranges where the events say nothing. Dropping the page because it has
 * no events would hide data that exists — the opposite failure to the one the tree is for.
 */
function withDetailPages(tree: PageNode[]): PageNode[] {
  const present = new Set(tree.map((page) => page.key));
  const missing = PAGES_WITH_DETAIL.filter((key) => !present.has(key)).map((key) => ({
    ...pageMeta(key),
    total: 0,
    users: null,
    tabs: [],
    features: [],
    share: 0,
  }));
  return [...tree, ...missing];
}

type BoardTab = 'usage' | 'sourcing';
/** The open tab lives in the URL (`?tab=sourcing`), so a link can open the Sourcing tab directly. */
const tabFromUrl = (): BoardTab => (new URLSearchParams(window.location.search).get('tab') === 'sourcing' ? 'sourcing' : 'usage');

/**
 * Usage Analytics with its two tabs (Oct 26 2026): how People Space is used, and the outreach
 * Sourcing funnel — its own tab because it measures what sourcing achieved, not clicks.
 */
export default function UsageAnalyticsView() {
  const [tab, setTab] = useState<BoardTab>(tabFromUrl);
  const pick = (next: BoardTab) => {
    setTab(next);
    const url = new URL(window.location.href);
    if (next === 'sourcing') url.searchParams.set('tab', 'sourcing');
    else url.searchParams.delete('tab');
    window.history.replaceState(window.history.state, '', url);
    void logAnalyticsEvent('usage_analytics_tab_opened', { tab: next });
  };
  return (
    <div className="mx-auto max-w-6xl space-y-4 p-1">
      <div role="tablist" className="flex gap-1 border-b border-zinc-200">
        {(['usage', 'sourcing'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => pick(t)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === t ? 'border-zinc-900 font-semibold text-zinc-900' : 'border-transparent text-zinc-500'}`}
          >
            {t === 'usage' ? 'Usage' : 'Sourcing'}
          </button>
        ))}
      </div>
      {tab === 'sourcing' ? <SourcingAnalytics /> : <UsageBoard />}
    </div>
  );
}
